import { getHelipadFee, routePadFeesNet, routePaxFeesNet, buildPadTouches, sightseeingPadTouches } from './pricing/helipad-fee.js';
import { matchRouteOverride, overrideFlightNet } from './pricing/route-override.js';
import { matchSightseeingOverride, sightseeingOverrideNet } from './pricing/sightseeing-override.js';
import { priceRouteAuto, legTime } from './pricing/route-engine.js';
import { cityCoords, haversineKm } from './flightTime.js';
import { resolveBaseForDate } from './fleet-availability.js';
import { feeClassOf } from './pricing/fee-class.js';

// op-pricing P6b — resolve an AUTOMATIC aircraft's broker route amount via the
// SAME engine the operator dashboard/simulator + client customer-path use.
// Returns the time-based routeAmount (rotor+fix+min, fees separate), or null if
// not auto / no rate / missing coords. ctx = { base, speedKmh, pickup, dropoff }.
function autoBrokerAmount(acCfg, buffer, ctx, roundTrip = false) {
  if (!acCfg || acCfg.pricingModel !== 'auto') return null;
  const rate = Number(acCfg.ratePerHour) || 0;
  if (!(rate > 0)) return null;
  if (!ctx || !ctx.base || !(Number(ctx.speedKmh) > 0) || !ctx.pickup || !ctx.dropoff) return null;
  // true per-leg round trip: roundTrip → base→A→B→A→base (1 ferry out + 2 pax +
  // 1 ferry back), NOT 2× the one-way loop. The phantom-ferry overcount of the
  // old one-way×2 default is removed at exactly this geometry choice.
  const res = priceRouteAuto({
    base: ctx.base, stops: [ctx.pickup, ctx.dropoff], roundTrip,
    speedKmh: Number(ctx.speedKmh), ratePerHour: rate,
    feeFor: () => ({ brokerFee: null, source: 'none' }),
    minimumPerBooking: acCfg.minimumPerBooking, fixedPerBooking: acCfg.fixedPerBooking,
    buffer, applyBlockBuffer: !!acCfg.applyBlockBuffer, countBaseAsFee: !!acCfg.countBaseAsFee,
    // Automatic block time floored to whole minutes (cost-model parity, 2026-06-15):
    // floor(Σ legs) once — RT = floor(2×oneWayRaw), like cost-model (≤1 min > 2× one-way).
    floorBlockMin: true,
  });
  return res.ok ? res.breakdown.routeAmount : null;
}

// ═══════════════════════════════════════════════════════════════════════
// Phase 1b — COST MODEL (pricingModel = 'costModel'). Operator derives their
// NET payout (Operator-Netto) from cost components + margins; our commission
// markup + VAT are applied on top by the SAME brokerToCustomer the other modes
// use (NO second compute path) — costBrokerAmount returns the Operator-Netto
// that slots into the exact same `broker` variable as auto's routeAmount.
//
// All inputs NET. Empty / ≤0 component = not counted. ALL empty (Operator-Netto
// ≤ 0) → returns null → resolves to "price on request" (never €0). Identical
// math client + server (App.jsx __CMS mirrors this).
//
// Sign convention (DISCOUNTS): tier/brokerDiscount `value` is SIGNED — negative
// = discount, positive = surcharge. pct → ×(1 + value/100); abs → + value €.
// ═══════════════════════════════════════════════════════════════════════
const nNum = (v) => (Number(v) > 0 ? Number(v) : 0); // empty/≤0 → 0 (not counted)

function applyCostDiscount(amount, disc) {
  if (!disc || disc.value == null || !Number.isFinite(Number(disc.value))) return amount;
  const v = Number(disc.value);
  if (disc.type === 'abs') return amount + v;          // signed: −100 = −€100 discount
  return amount * (1 + v / 100);                        // pct (default): signed %
}

// ── HELISTAR "Special discount" (the operator's `brokerDiscount`) ───────────
// A NET-STAGE operation, NOT a DOC discount: the LAST adjustment applied to the
// COMPLETE Operator-Net (flight DOC chain + per-pad Airport&Handling + per-pax
// fees), just before the operator's own VAT / the €100 payout-rounding and the
// platform markup. SIGNED (− = discount, + = surcharge); null/empty → no-op.
// This is DISTINCT from the flight-time TIER discount (discountTiers[]), which is
// deducted from the DOC BEFORE the DOC margin. Folding the special into the DOC
// (pre-2026-06-16) discounted the small DOC instead of the large Net and skipped
// the pad/pax fees → Operator-Final too high vs HELISTAR. costBrokerBreakdown's
// `netto` is therefore the Net BEFORE the special; the downstream combined-net
// assembly (customerBaseTotal / computeExpectedTotal / sightseeingBrokerNet / the
// simulator) adds pad + pax then applies the special EXACTLY ONCE via these helpers.
export function applyBrokerSpecial(net, brokerDiscount) {
  if (!(net > 0)) return net;
  const v = applyCostDiscount(net, brokerDiscount);
  return v > 0 ? v : 0;
}
// Signed € delta of the special on a given net (for the simulator's own line).
export function brokerSpecialDelta(net, brokerDiscount) {
  if (!(net > 0)) return 0;
  return applyBrokerSpecial(net, brokerDiscount) - net;
}

// Block flight-hours for the cost model — SAME engine path as Automatic
// (base→pickup→drop-off→base). Aircraft without homeBase → direct pad-to-pad
// (no positioning/return ferry). Returns { hours, legCount } | null.
//
// HELISTAR parity (Phase 2, 2026-06-10): the operator's own calc rounds the block
// time of ONE one-way base round to WHOLE MINUTES, then multiplies by the number
// of rounds for a round trip. So we round the ONE-WAY block here (Math.round on
// minutes), then × rounds — NOT the raw RT block. This keeps RT == EXACTLY 2× one-
// way (round(oneway)×2 = 2×round(oneway)) AND makes fuel/fixedOps/crew match the
// operator (e.g. HGH LGAV→Antiparos block 73 min, not 73.31). route-engine.js stays
// RAW (its totalBlockMin still Σ raw leg minutes — the Automatic rotor cost + the
// customer "~min/~km" display round separately); the whole-minute rounding lives
// ONLY here on the cost-model one-way block. legCount = PHYSICAL legs flown (km > 0)
// per one-way × rounds (a 0-km base==pickup positioning leg is not billed; a
// far-base positioning ferry is).
function costFlightHours(acCfg, buffer, ctx, roundTrip = false) {
  if (!ctx || !(Number(ctx.speedKmh) > 0) || !ctx.pickup || !ctx.dropoff) return null;
  // Operator-defined per-leg overhead (taxi/approach) enters the PRICE only when
  // the operator toggled "also price". 'display-only' overhead never reaches here.
  const cm = acCfg?.costModel || {};
  const ovh = (cm.overheadAffects === 'price' && Number(cm.overheadMinutes) > 0) ? Number(cm.overheadMinutes) : 0;
  const rounds = roundTrip ? 2 : 1;
  if (ctx.base && ctx.base.lat != null && ctx.base.lng != null) {
    // ALWAYS the ONE-WAY geometry; round its block to whole minutes, then × rounds.
    const res = priceRouteAuto({
      base: ctx.base, stops: [ctx.pickup, ctx.dropoff], roundTrip: false,
      speedKmh: Number(ctx.speedKmh), ratePerHour: 1, // dummy rate — we only read block time
      feeFor: () => ({ brokerFee: null, source: 'none' }),
      buffer, applyBlockBuffer: !!acCfg.applyBlockBuffer, countBaseAsFee: !!acCfg.countBaseAsFee,
      overheadMin: ovh,
    });
    if (!res.ok) return null;
    // A) HELISTAR: FLOOR the TOTAL trip block to whole minutes (one-way × rounds),
    // NOT per-one-way then ×rounds. HELISTAR rounds the whole trip down once
    // (verified: 75.59→75 one-way; 115.08→115 and 151.18→151 RT). Flooring the
    // one-way first truncated the sub-minute remainder before doubling (114/150).
    // Consequence: RT block = floor(2×oneWayRaw), which is NOT always 2×floor(oneWayRaw)
    // — HELISTAR itself isn't (its RT 115 ≠ 2×114); the raw geometry still doubles.
    const blockMin = Math.floor(res.breakdown.totalBlockMin * rounds);
    const oneWayLegCount = res.breakdown.legs.filter(l => Number(l.km) > 0).length;
    return { hours: blockMin / 60, legCount: oneWayLegCount * rounds };
  }
  // no-base fallback: direct pad-to-pad (1 physical leg one-way). FLOOR the TOTAL
  // trip block (one-way × rounds) to whole minutes — same HELISTAR rule as above.
  const lt = legTime(ctx.pickup, ctx.dropoff, Number(ctx.speedKmh), 0, ovh);
  if (!lt) return null;
  const blockMin = Math.floor(lt.blockMin * rounds);
  return { hours: blockMin / 60, legCount: rounds };
}

// Sightseeing geometry (Phase 2 sightseeing): a loop departing AND landing at
// the chosen helipad (ctx.pickup). The revenue "leg" is a duration-block at the
// pad — flightHours = the chosen tour duration, NOT distance/speed (distance 0).
// Positioning: if the aircraft is based elsewhere it ferries home base → pad
// and pad → home base (geometry-priced), so island sightseeing from a non-based
// pad is automatically dearer. base AT the pad (or no base) → no positioning.
//   → { posMin, totalHours: (posMin + duration)/60, legs: posMin>0 ? 3 : 1 }
function sightseeingGeometry(ctx, durationMin) {
  const dur = Number(durationMin);
  if (!ctx || !ctx.pickup || !(dur > 0)) return null;
  let posMin = 0;
  if (ctx.base && ctx.base.lat != null && Number(ctx.speedKmh) > 0) {
    const km = haversineKm(ctx.base, ctx.pickup);
    if (km != null && km >= 1) { // base is NOT at the chosen helipad → ferry there + back
      const out = legTime(ctx.base, ctx.pickup, Number(ctx.speedKmh), 0);
      const back = legTime(ctx.pickup, ctx.base, Number(ctx.speedKmh), 0);
      // legTime returns RAW minutes — FLOOR the positioning total ONCE, matching the
      // cost-model block-time rule (costFlightHours Math.floor) for HELISTAR parity
      // (e.g. LGAV 64.x → 64, not round → 65). Was Math.round until 2026-06-16.
      if (out && back) posMin = Math.floor(out.blockMin + back.blockMin);
    }
  }
  return { posMin, totalHours: (posMin + dur) / 60, legs: posMin > 0 ? 3 : 1 };
}

// Resolve the cost-model Operator-Netto (steps 1–6 of the chain). legs = revenue
// legs (one-way 1); pax for per-pax additional fees (default 1). Steps 7–9
// (markup + VAT + rounding) are done downstream by brokerToCustomer.
//
// sightseeing = { durationMin } (Phase 2): when set, the flight hours come from
// the loop geometry (positioning + chosen duration) instead of the pickup→dropoff
// route, legFee counts the positioning legs, and the flat NET `sightseeingRate`
// is added as a no-margin additional line (duration scales price only via the
// hourly costs — the rate itself is flat). Sightseeing is ALWAYS compute (no
// manual/override price).
// Full NET cost-model chain as a structured breakdown — the SINGLE SOURCE of
// truth for both the Operator-Netto number (costBrokerAmount) and the simulator
// component panel. NET-ONLY: this never computes markup / commission /
// brokerToCustomer / customer total. `vat`/`final` are the operator's OWN VAT on
// their OWN net (their invoice gross), NOT the customer VAT. Returns null when
// all components are empty (→ "price on request").
export function costBrokerBreakdown(acCfg, buffer, ctx, legs = 1, pax = 1, roundTrip = false, sightseeing = null) {
  if (!acCfg || acCfg.pricingModel !== 'costModel') return null;
  const cm = acCfg.costModel || {};
  const fees = acCfg.fees || {};
  const add = acCfg.additional || {};
  const air = acCfg.airport || {};
  let flightHours, L;
  if (sightseeing && Number(sightseeing.durationMin) > 0) {
    const ss = sightseeingGeometry(ctx, sightseeing.durationMin);
    if (ss == null) return null;
    flightHours = ss.totalHours;
    L = ss.legs; // legFee × all legs incl. positioning
  } else {
    const fh = costFlightHours(acCfg, buffer, ctx, roundTrip);
    if (fh == null) return null;
    flightHours = fh.hours;
    // Leg-fee count = PHYSICAL legs flown (single source from the geometry):
    // a 0-km base==pickup positioning leg is not billed, a far-base ferry is.
    // The caller's `legs` param is now only a degenerate fallback.
    L = fh.legCount > 0 ? fh.legCount : (Number(legs) > 0 ? Number(legs) : 1);
  }
  const P = Number(pax) > 0 ? Number(pax) : 1;
  // Round trip = the one-way base-round flown twice (buildSequence). flightHours +
  // legCount (→ fuel/fixedOps/crew/legFee) are ALREADY doubled by the geometry
  // above. The FLAT per-booking components the geometry does NOT touch —
  // grossMargin, the additional fees, and the cost-model airport{4} — are scaled
  // ×rounds here so the whole round trip is 2× one-way (Helistar parity:
  // GM 1400→2800, airport 936→1872, additional 40→80). bookingFee stays FLAT (once
  // per booking). The flight-time discount tier + DOC-margin% re-evaluate on the
  // doubled flightHours/DOC automatically (the 4h band), so they are NOT scaled.
  const rounds = roundTrip ? 2 : 1;

  // 1) Direct operating cost. Fuel has two equivalent input modes (operator
  // picks ONE): 'perhour' = a direct fuel €/flight-hour; 'burnprice' (default,
  // back-compat for every existing config) = fuel burn (kg/h) × fuel price
  // (€/kg). Both normalise to the SAME fuel-€/h here — single source, no
  // parallel path. Unset/legacy mode → 'burnprice' so existing configs are
  // unchanged.
  const fuelPerHour = cm.fuelMode === 'perhour'
    ? nNum(cm.fuelCostPerHour)
    : nNum(cm.fuelBurnKgPerHour) * nNum(cm.fuelPricePerKg);
  const fuelCost = fuelPerHour * flightHours;
  const fixedOpsCost = nNum(cm.fixedOpsPerHour) * flightHours;
  const crewCost = nNum(cm.crewVarPerHour) * flightHours;
  const bookingFee = nNum(fees.bookingFee);
  const legFeeTotal = nNum(fees.legFee) * L;
  const DOC = bookingFee + legFeeTotal + fuelCost + fixedOpsCost + crewCost;
  // 2) Flight-time TIER discount ONLY (by flightHours) — deducted from the DOC
  // BEFORE the DOC margin. The operator's "Broker special discount"
  // (acCfg.brokerDiscount) is deliberately NOT applied here: it is a NET-stage
  // operation (see applyBrokerSpecial above), the LAST step on the COMPLETE
  // Operator-Net (flight + pad + per-pax fees), applied downstream. So this
  // breakdown's `netto` is the Operator-Net BEFORE the special — the single source
  // the combined-net assembly adds pad/pax to, then applies the special exactly once.
  // Two-pass boundary (2026-06-16, HELISTAR parity, shared charter+sightseeing):
  // Pass 1 = the canonical half-open band [fromH, toH) — every mid-band value AND every
  //   contiguous internal boundary keeps its current band (h=0.7 → {0.7,1} via fromH,
  //   not {0.5,0.7}; open top band toH==null matches h≥fromH).
  // Pass 2 (ONLY if pass 1 found nothing) = inclusive upper edge [fromH, toH] — catches
  //   the ORPHAN top edge where h exactly equals a band's toH and no band starts there
  //   (e.g. a base==pad 1.0h sightseeing tour: {0.7,1} with a gap to {2.5,…} → −3%).
  // Purely ADDITIVE: it can only grant a discount where pass 1 matched none; it never
  // changes an already-matched discount, so no existing charter price moves.
  const tiers = acCfg.discountTiers || [];
  const inFrom = (t) => t && Number(t.fromH) <= flightHours;
  let tier = tiers.find(t => inFrom(t) && (t.toH == null || t.toH === '' || flightHours < Number(t.toH)));
  if (!tier) tier = tiers.find(t => inFrom(t) && t.toH != null && t.toH !== '' && flightHours <= Number(t.toH));
  let discounted = applyCostDiscount(DOC, tier);
  if (!(discounted > 0)) discounted = 0;
  // 3) DOC margin — ONLY on discounted DOC
  const docTotal = discounted * (1 + nNum(cm.docMarginPct) / 100);
  // 4) additional (no margin) — incl. the flat NET sightseeingRate for a
  // sightseeing booking (added once, NOT × hours; the hourly costs above already
  // scale price with the chosen duration). Analogous to the per-pax fees.
  const additional = (nNum(add.waitingPerHour) * nNum(add.waitingHours)
    + (nNum(add.passengerFeePerPax) + nNum(add.landingFeePerPax) + nNum(add.departureFeePerPax)) * P) * rounds
    + (sightseeing ? nNum(acCfg.sightseeingRate) : 0); // sightseeing is always one-way (rounds=1)
  // 5) pad fees (no margin) — per-pad, per-direction NET Σ resolved by the caller
  // along the REAL legs (ctx.padFeesNet) — ONE source for all 3 modes. Falls back
  // to the DEPRECATED flat cost-model airport{4} only when padFeesNet is absent
  // (transition until migration), so nothing regresses pre-wiring.
  const airport = (ctx && ctx.padFeesNet != null)
    ? nNum(ctx.padFeesNet) // already per-direction (RT doubled by buildPadTouches) — not ×rounds
    : (nNum(air.airportDeparture) + nNum(air.airportLanding) + nNum(air.handling) + nNum(air.special)) * rounds;
  // 6) + grossMargin → Operator-Netto
  const grossMargin = nNum(cm.grossMargin) * rounds;
  const netto = docTotal + additional + airport + grossMargin;
  if (!(netto > 0)) return null; // all-empty → null → price on request
  // Operator's OWN invoice VAT: applied to the cent-rounded net (standard invoice
  // rounding) so net + VAT add up exactly. NOT the customer VAT/markup.
  const nettoR = Math.round(netto * 100) / 100;
  const vat = Math.round(nettoR * VAT_RATE * 100) / 100;
  return {
    flightHours, legCount: L,
    fuelCost, fixedOpsCost, crewCost, bookingFee, legFeeTotal, doc: DOC,
    // Flight-time TIER discount only (on the DOC, before margin). The broker special
    // discount is NOT here — it is a NET-stage op applied downstream (applyBrokerSpecial).
    // signed: < 0 = discount, > 0 = surcharge, 0 = none set
    discountDelta: discounted - DOC,
    // The breakdown `netto` is the Operator-Net BEFORE the broker special discount.
    brokerDiscount: acCfg.brokerDiscount || null,
    discounted, docMarginPct: nNum(cm.docMarginPct), docMarginAmount: docTotal - discounted, docTotal,
    additional, airport, grossMargin,
    netto, vat,
    final: Math.round((nettoR + vat) * 100) / 100,
    // Model B (2026-06-13, Helistar parity) — the Operator-Final incl VAT rounded
    // UP to the next €100; this is the amount the operator is actually paid and the
    // base the platform markup computes on. See operatorFinalRounded below.
    roundedFinal: nettoR > 0 ? Math.ceil((Math.round((nettoR + vat) * 100) / 100) / 100) * 100 : 0,
  };
}

// Operator-Netto only (steps 1–6). Thin wrapper over the single-source
// breakdown — every existing caller is unchanged.
export function costBrokerAmount(acCfg, buffer, ctx, legs = 1, pax = 1, roundTrip = false, sightseeing = null) {
  const b = costBrokerBreakdown(acCfg, buffer, ctx, legs, pax, roundTrip, sightseeing);
  return b ? b.netto : null;
}

// ── Model B — Operator-Final-incl-VAT rounded UP to the next €100 ───────────
// (cost-model + automatic only; manual / route-override are UNCHANGED). Owner
// decision 2026-06-13: the operator is paid their charter Final incl VAT 13%
// rounded up to the next 100, and the platform commission markup computes on
// THAT rounded value (mirrors Helistar "Total Gross → Final Price"). This is the
// SINGLE canonical rounding point — every consumer (simulator, /api/aircraft/quote,
// real booking, operator-payout record, commission invoice) funnels the
// compute-mode broker NET through effectiveComputeNet() so they all share the
// identical rounded Final with NO further per-site math and NO double-VAT.
//
// operatorFinalRounded(net): net (excl VAT) → round(net×1.13) [the operator
//   invoice gross, cent-rounded] → ceil(/100)×100. 0-safe.
export function operatorFinalRounded(net) {
  if (!(net > 0)) return 0;
  const finalInclVat = Math.round(net * (1 + VAT_RATE) * 100) / 100;
  return Math.ceil(finalInclVat / 100) * 100;
}

// effectiveComputeNet(net): substitute the raw compute-mode NET with the NET
// whose ×1.13 gross IS the rounded Final (= roundedFinal / 1.13). Feeding this
// into the EXISTING chain reproduces Model B exactly with zero downstream
// changes: brokerToCustomer(effNet) = ceil(roundedFinal×(1+rate)/step)×step (VAT
// already inside roundedFinal → no second VAT), and round(effNet×1.13) =
// roundedFinal (the operator payout). null/0 passes through unchanged so the
// COMPUTE-TRIGGER semantics (rtBroker null without speedKmh → one-way×2) hold.
export function effectiveComputeNet(net) {
  if (!(net > 0)) return net;
  return operatorFinalRounded(net) / (1 + VAT_RATE);
}

// Generate a _key for Sanity array-of-object items. Kept local to avoid
// src/lib → src/pages/api dependency; mirrors the helper in _helpers.js.
const newKey = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
};

// Shared pricing logic for the HeliGreece booking flow.
//
// WHY THIS EXISTS
//   The server (`src/pages/api/bookings-create.js`) needs to validate the
//   client-submitted `totalPrice` against an independently-computed expected
//   value. Without this check, an attacker could POST `totalPrice: 1` and
//   pass that directly to Mollie as the authorize amount (see security
//   audit finding H-A2).
//
// FRONTEND MIRROR
//   The algorithms here mirror the client-side logic in
//   `src/components/App.jsx` at these locations:
//     - Lines 1227-1270  estimatePrice()
//     - Lines 5181-5194  calcExtrasTotal()
//     - Lines 20-34      route.pricing flattening inside __CMS loader
//   A future refactor will make App.jsx import these helpers directly, but
//   for now the code is intentionally duplicated so the backend can enforce
//   price integrity without waiting on the 11k-line frontend refactor.
//
// TOLERANCE STRATEGY
//   `expected.type === 'exact'`: 2 % tolerance. Exact prices come straight
//       from CMS pricing rows; client + server should agree bit-for-bit.
//   `expected.type === 'estimated'`: 5 % tolerance. Estimated prices are
//       derived via interpolation or hourly-rate fallback — both involve
//       Math.ceil rounding to €10 and floating-point ops that are identical
//       across V8 runtimes in theory but deserve a defensive margin.
//   `expected.type === 'indicative'`: the frontend forces isQuoteRequest
//       when the price is indicative, so Mollie-authorize is skipped; no
//       server validation runs on that path.
//
// ROUNDING NOTES
//   - Customer price derived from brokerPrice:
//       Math.ceil(brokerPrice / (1 - commission) / 10) * 10   (round UP to €10)
//   - Interpolated rate:
//       Math.ceil(rate * flightMin / 10) * 10                  (round UP to €10)
//   - Hourly-rate fallback:
//       Math.ceil(rate * flightMin / 60 / 10) * 10             (round UP to €10)


/**
 * Single source of truth for the return-trip customer-facing price.
 *
 * Used identically by the client (App.jsx __CMS loader + estimatePrice) and
 * the server (computeExpectedTotal anti-tampering check) so the two never
 * drift. If the winning pricing row carries an explicit `returnBrokerPrice`,
 * the return price is derived from it via the same commission + round-up-to-€10
 * math as the one-way price. Otherwise the legacy `one-way × 2` applies — so
 * routes without `returnBrokerPrice` are fully backward-compatible.
 *
 * @param {Object|null} pricingEntry — the winning pricing row, may carry
 *   `returnBrokerPrice`. null/absent → legacy × 2.
 * @param {number} onewayCustomerPrice — the resolved one-way customer price.
 * @param {number} commissionPct — operator/default commission % (default 10).
 * @returns {number} return-trip customer price.
 */
export function getReturnPrice(pricingEntry, onewayCustomerPrice, rate = 10, roundStep = 10, roundTripPrice = null) {
  // Emergency kill-switch — set globalThis.FEATURE_EXPLICIT_RETURN_PRICING
  // = false to revert to legacy one-way × 2 without a re-deploy.
  if (typeof globalThis !== 'undefined' && globalThis.FEATURE_EXPLICIT_RETURN_PRICING === false) {
    return onewayCustomerPrice * 2;
  }
  // Precedence (single choke point — client + server identical):
  //   1. explicit returnBrokerPrice override → operator-set round-trip netto.
  //   2. compute round-trip price (true A→B→A, phantom ferry removed) when the
  //      engine produced one (auto/cost-model WITH speedKmh).
  //   3. legacy one-way × 2 (manual, or auto/cost without speedKmh).
  const rbp = Number(pricingEntry?.returnBrokerPrice);
  if (Number.isFinite(rbp) && rbp > 0) {
    // P2 #9 phase 4 — broker-markup model (was rbp / (1 − rate)).
    return brokerToCustomer(rbp, rate, VAT_RATE, roundStep);
  }
  if (roundTripPrice != null && roundTripPrice > 0) return roundTripPrice;
  return onewayCustomerPrice * 2;
}

/**
 * Compute the customer price for a specific aircraft on a single route doc.
 *
 * Walks `route.pricing[].prices[]` (flattened across operators), keeping the
 * MINIMUM customer price per aircraft — matching the frontend's __CMS loader.
 * If `price` (customer price) is missing, derives from `brokerPrice` via
 * `Math.ceil(brokerPrice / (1 - commPct/100) / 10) * 10`.
 *
 * @param {string} aircraftId
 * @param {Object} route — Sanity route doc. `route.pricing[].prices[].aircraft`
 *   MUST be a string ID (not a reference) — caller is responsible for
 *   projecting `"aircraft": aircraft->id` in the GROQ query.
 * @param {number} commPct — default commission percentage (0-100), used only
 *   when falling back to brokerPrice-derived price.
 * @param {Function} [operatorFilter] — optional `(operatorId) => boolean`.
 *   When provided, every pricing row whose operator fails the predicate is
 *   skipped, so the resolved price reflects ONLY operators that pass — Brief A
 *   passes a predicate restricting to operators that serve BOTH chosen helipads,
 *   making the pad-exact customer price (client) and the anti-tampering
 *   expected price (server) identical by construction. A row with no
 *   operatorId fails the predicate when a filter is active (defensive: an
 *   unattributable price can't be validated against served pads).
 * @returns {{price: number|null, type: 'exact'|'indicative'|null}}
 */
export function getRoutePriceFromDoc(aircraftId, route, settingsRate, autoCtx, operatorFilter, fromId = null, toId = null) {
  const EMPTY = { price: null, type: null, returnBrokerPrice: null, brokerPrice: null, operator: null, rate: null };
  if (!route || !aircraftId) return EMPTY;

  let minPrice = null;
  let minBroker = null;
  // returnBrokerPrice of the row that wins on one-way price — getReturnPrice
  // applies it. The client __CMS loader uses the SAME "winning one-way row's
  // returnBrokerPrice" rule so the two never drift.
  let minReturnBroker = null;
  // Winning operator's helipad-fee data (Option Y) + effective rate.
  let minOperator = null;
  let minRate = null;
  let minRoundStep = 10; // Phase 1b — winning operator's customer-price rounding step
  // Per-leg round trip — the winning row's TRUE A→B→A broker netto + the
  // customer round-trip price derived from it (compute modes only; null →
  // getReturnPrice falls back to one-way×2 for manual / no-speedKmh).
  let minRoundTripBroker = null;
  let minRoundTripPrice = null;
  // D) Model B — true for compute modes (cost-model / automatic): the operator
  // Final incl VAT (incl. pad + pax fees) is rounded UP to €100 at the END (in
  // customerBaseTotal / computeExpectedTotal), and the markup computes on it.
  // false for manual / route-override (never rounded). brokerPrice below is RAW
  // (no effNet) so the end-rounding sees the true combined net incl. fees.
  let minRoundFinal100 = false;
  // Broker special discount of the WINNING row — applied to the COMBINED net
  // (flight + pad + pax) downstream by customerBaseTotal / computeExpectedTotal.
  // Only a pure cost-model compute carries it (the special is a cost-model concept);
  // manual / override / auto rows → null (no special).
  let minBrokerDiscount = null;
  // Brief 2 — landing-fee source rule. location.fee applies to the customer
  // total whenever the flight price is a ROUTE price (manual / auto-computed /
  // override); it is IGNORED only for a pure cost-model compute (pricingModel
  // costModel AND no override), whose pad fees come from the cost-model chain
  // itself (aircraftPricing.airport/additional). Tracked per winning row.
  let minFeeFromLocation = true;
  // op-pricing P6b — resolve broker per (operator, aircraft) with the SAME
  // priority chain as the client/simulator: override > manual brokerPrice >
  // live-auto (engine). brokerToCustomer applies the markup once, downstream.
  for (const op of route.pricing || []) {
    // Brief A — pad-service / both-pads-served gate. Skip operators failing the
    // predicate so client + server resolve the identical pad-exact price.
    if (operatorFilter && !operatorFilter(op?.operatorId)) continue;
    // Operator-boundary (2026-06-17): an operator may price an aircraft ONLY if it
    // FLEET-OWNS it. Candidate entries carry op.fleet (route-doc entries do not →
    // guard is a no-op there, backward-compatible). Without this, a route-serving
    // operator's flat-price override (aircraftRef null = "all MY aircraft") leaked
    // onto ANOTHER operator's aircraft — live bug: HELISTAR's €5000 LGAV→ZZZZANT
    // override priced Bellavia's bell206 (HELISTAR does not own it). With no
    // owning+pricing operator the aircraft falls through to EMPTY → "Price on request".
    if (Array.isArray(op.fleet) && !op.fleet.includes(aircraftId)) continue;
    const rate = op?.commissionRate ?? settingsRate ?? 10;
    // Phase 1b — operator customer-price rounding step (€10 default / 50 / 100),
    // applied once at the end inside brokerToCustomer. Operator-wide (affects all
    // of this operator's modes); unset → 10 → no change to Manual/Automatic.
    const roundStep = op?.priceRoundingStep || 10;
    const acCfg = (op.acPricing || []).find(a => a && a.aircraft === aircraftId) || null;
    const consider = (broker, returnBroker, rtBroker, source) => {
      if (!(broker > 0)) return;
      // D) compute modes (cost-model / automatic) round the operator Final to €100.
      // The flight-only customer `price` reflects that rounding (effFor), but the
      // stored brokerPrice stays RAW so the END-rounding in customerBaseTotal can
      // round the COMBINED net (flight + pad + pax) — fixing the fees-escape-rounding
      // bug. Manual / override pass through raw (round100 false).
      const round100 = (source === 'costmodel' || source === 'auto');
      const effFor = (n) => (round100 && n > 0) ? effectiveComputeNet(n) : n;
      const cp = brokerToCustomer(effFor(broker), rate, VAT_RATE, roundStep);
      if (cp != null && (minPrice == null || cp < minPrice)) {
        minPrice = cp; minBroker = broker; // RAW (no effNet) — see customerBaseTotal
        minRoundFinal100 = round100;
        // location.fee applies for every route-price source; ignored only for a
        // pure cost-model compute (an override on a cost-model aircraft is a
        // route price → fees apply, per G-B price-source rule).
        minFeeFromLocation = (source !== 'costmodel');
        // The broker special discount only rides a pure cost-model compute (its
        // flight net came from costBrokerBreakdown, which now excludes the special).
        // override/manual/auto → null (no special on those flight nets).
        minBrokerDiscount = (source === 'costmodel' && acCfg) ? (acCfg.brokerDiscount || null) : null;
        minReturnBroker = (returnBroker != null) ? returnBroker : null;
        // compute-mode round trip: derive the customer round-trip price from the
        // SAME winning row's true A→B→A netto (no ×2). null for manual → ×2.
        minRoundTripBroker = (rtBroker != null && rtBroker > 0) ? rtBroker : null;
        minRoundTripPrice = (minRoundTripBroker != null)
          ? brokerToCustomer(effFor(minRoundTripBroker), rate, VAT_RATE, roundStep) : null;
        minRate = rate;
        minRoundStep = roundStep;
        minOperator = {
          _id: op.operatorId || null,
          commissionRate: (typeof op.commissionRate === 'number') ? op.commissionRate : null,
          helipadOverrides: Array.isArray(op.helipadOverrides) ? op.helipadOverrides : [],
          // Operator-wide opt-in: charge landing fees on empty positioning/ferry
          // legs too. Read by buildPadTouches (opForHelipad). Default false.
          chargeFerryLandings: !!op.chargeFerryLandings,
        };
      }
    };
    // Route-override (operator special-condition flat NET price) — HIGHEST
    // priority: it REPLACES the cost-model/auto/manual FLIGHT net for the matched
    // pads. Pad fees still apply automatically ON TOP downstream (routePadFeesNet),
    // and the markup is applied ONCE (brokerToCustomer) like every other source —
    // so override + pad fee combine in one final rounding, client == server. RT =
    // roundTripNet if set, else the C additive-delta from the locked cost-model
    // geometry (overrideFlightNet). Source 'override' → route price (fees apply).
    const routeOverride = matchRouteOverride(op.routeOverrides, fromId, toId, aircraftId);
    if (routeOverride) {
      let cmOw = null, cmRt = null;
      if (acCfg?.pricingModel === 'costModel') {
        cmOw = costBrokerAmount(acCfg, op.buffer, autoCtx, 1, 1, false);
        cmRt = costBrokerAmount(acCfg, op.buffer, autoCtx, 2, 1, true);
      } else if (acCfg?.pricingModel === 'auto') {
        cmOw = autoBrokerAmount(acCfg, op.buffer, autoCtx, false);
        cmRt = autoBrokerAmount(acCfg, op.buffer, autoCtx, true);
      }
      consider(overrideFlightNet(routeOverride, false, cmOw, cmRt), null, overrideFlightNet(routeOverride, true, cmOw, cmRt), 'override');
      continue; // override is authoritative for this operator-aircraft
    }
    let matchedRow = false;
    for (const p of op.prices || []) {
      if (p.aircraft !== aircraftId) continue;
      matchedRow = true;
      let broker = null, rtBroker = null, source = null;
      if (p.overrideBrokerPrice != null && p.overrideBrokerPrice > 0) {
        broker = Number(p.overrideBrokerPrice); source = 'override'; // route price → location.fee applies
      } else if (acCfg?.pricingModel === 'auto') {
        // RAW broker net — the €100 rounding (Model B) is applied at the END on the
        // combined net (flight+pad+pax) by consider()/customerBaseTotal, not here.
        broker = autoBrokerAmount(acCfg, op.buffer, autoCtx, false);
        rtBroker = autoBrokerAmount(acCfg, op.buffer, autoCtx, true);   // COMPUTE-TRIGGER: null without speedKmh
        source = 'auto';
      } else if (acCfg?.pricingModel === 'costModel') {
        broker = costBrokerAmount(acCfg, op.buffer, autoCtx, 1, 1, false);
        rtBroker = costBrokerAmount(acCfg, op.buffer, autoCtx, 2, 1, true); // 2 revenue legs (A→B, B→A)
        source = 'costmodel'; // pure compute → location.fee IGNORED (fees from cost-model chain)
      } else if (p.brokerPrice) {
        broker = Number(p.brokerPrice); source = 'manual'; // route price → location.fee applies
      }
      consider(broker, p.returnBrokerPrice, rtBroker, source);
    }
    // Auto / cost-model aircraft with NO price row for this route → still compute.
    if (!matchedRow && acCfg?.pricingModel === 'auto') {
      // RAW broker — €100 rounding applied at the END (consider/customerBaseTotal).
      consider(autoBrokerAmount(acCfg, op.buffer, autoCtx, false), null, autoBrokerAmount(acCfg, op.buffer, autoCtx, true), 'auto');
    } else if (!matchedRow && acCfg?.pricingModel === 'costModel') {
      consider(costBrokerAmount(acCfg, op.buffer, autoCtx, 1, 1, false), null, costBrokerAmount(acCfg, op.buffer, autoCtx, 2, 1, true), 'costmodel');
    }
  }
  if (minPrice != null) {
    return { price: minPrice, type: 'exact', returnBrokerPrice: minReturnBroker, brokerPrice: minBroker, operator: minOperator, rate: minRate, roundStep: minRoundStep, roundTripPrice: minRoundTripPrice, roundTripBrokerNet: minRoundTripBroker, feeFromLocation: minFeeFromLocation, roundFinal100: minRoundFinal100, brokerDiscount: minBrokerDiscount };
  }

  const ind = (route.indicativePricing || []).find(x => x.aircraft === aircraftId);
  if (ind && ind.price) return { ...EMPTY, price: ind.price, type: 'indicative' };

  return EMPTY;
}

/**
 * Resolve the sightseeing customer price for an aircraft (Phase 2). Sightseeing
 * is ALWAYS compute via the cost-model chain — no manual/override price. Loops
 * over the operators that (a) offer sightseeing from the chosen helipad
 * (`op.sightseeingHelipads` contains `ctx.helipadId`) and (b) have this aircraft
 * `sightseeingEnabled`, keeping the MINIMUM customer price (same min rule as
 * getRoutePriceFromDoc). The flight hours come from the loop geometry
 * (positioning base→pad→base + chosen duration) and the flat `sightseeingRate`
 * is added in the cost-model chain. `feeFromLocation:false` so the Brief-2
 * location.fee side never applies (sightseeing pad fees live in the cost-model
 * airport chain — no double-count).
 *
 * @param {string} aircraftId
 * @param {Array}  operators — [{ operatorId, commissionRate, priceRoundingStep, buffer,
 *   sightseeingHelipads:[locationId], acPricing:[{ aircraft, sightseeingEnabled,
 *   sightseeingRate, costModel, fees, additional, airport, discountTiers, brokerDiscount }] }]
 * @param {Object} ctx — { base, speedKmh, pickup (chosen helipad coords), helipadId }
 * @param {number} durationMin — chosen tour duration (minutes)
 * @param {number} settingsRate — default commission %
 * @returns {{price, type, brokerPrice, operator, rate, roundStep, feeFromLocation}}
 */
// Shared sightseeing Operator-Netto (NET) — the SINGLE source for both the customer
// price (getSightseeingPrice → brokerToCustomer) and the operator simulator's NET
// preview, so they can never diverge. NET = flight + flat sightseeingRate (cost-model
// chain) + per-pad Airport & Handling + per-pax airport fees, all reusing the CHARTER
// fee functions (sightseeingPadTouches / routePadFeesNet / routePaxFeesNet) — no second
// math. GEOMETRY (HELISTAR-verified): the heli ferries from its BASE — base→pad→loop→
// pad→base — so the BASE is touched once each way (departure out + landing home) and the
// sightseeing pad TWICE (two ground stays → 2× departure, 2× landing, 2× handling); the
// per-pax fee applies once at the revenue loop departure. base == pad → a single loop
// stay (no positioning). Reuses sightseeingPadTouches for the exact touch list.
//   ctx.base   = the resolved base location doc ({ _id, name, lat, lng, padFees }).
//   ctx.pickup = the full pad doc ({ _id, name, lat, lng, padFees }).
//   ctx.padFeesNet:0 zeroes the DEPRECATED cost-model airport{} fallback so these
//   per-pad touch fees are the ONLY pad-fee source (no double-count).
// Returns { netto, flightNet, padNet, paxNet } or null (all-empty → price on request).
export function sightseeingBrokerNet(acCfg, buffer, ctx, durationMin, operator = null, feeClass = null, pax = 1, overrideFlatNet = null) {
  if (!acCfg || !ctx || !ctx.pickup || !(Number(durationMin) > 0)) return null;
  // Operator flat sightseeing price (sightseeingOverrides): when set it REPLACES the
  // ENTIRE cost-model flight computation (DOC, DOC-margin, flight-time discount, gross
  // margin AND the broker special discount) — owner spec: a flat is a fixed price, not a
  // line inside the cost-model chain. Only the per-pad/per-pax fees apply on top (they
  // are physical, independent of the flight price). No cost model needed when flat.
  const isFlat = overrideFlatNet != null && Number(overrideFlatNet) > 0;
  const flightNet = isFlat
    ? Number(overrideFlatNet)
    : costBrokerAmount({ ...acCfg, pricingModel: 'costModel' }, buffer, { ...ctx, padFeesNet: 0 }, 1, 1, false, { durationMin: Number(durationMin) });
  if (!(flightNet > 0)) return null;
  const touches = sightseeingPadTouches({ baseLocation: ctx.base, padLocation: ctx.pickup, operator });
  const padNet = routePadFeesNet(touches, feeClass);
  const paxNet = routePaxFeesNet(touches, feeClass, pax);
  // HELISTAR "Special discount" (operator broker special) — the LAST NET-stage op on the
  // COMPLETE cost-model net (flight + pad + per-pax). It belongs to the cost model, so a
  // flat price is exempt: with a flat active the operator net = flatNet + pad/pax fees,
  // nothing else. flightNet (cost-model branch) excludes it, so this is the single point.
  const netBeforeSpecial = flightNet + padNet + paxNet;
  const netto = isFlat ? netBeforeSpecial : applyBrokerSpecial(netBeforeSpecial, acCfg.brokerDiscount);
  return { netto, flightNet, padNet, paxNet, netBeforeSpecial, specialDelta: netto - netBeforeSpecial };
}

export function getSightseeingPrice(aircraftId, operators, ctx, durationMin, settingsRate, feeClass = null, pax = 1) {
  const EMPTY = { price: null, type: null, brokerPrice: null, operator: null, rate: null, roundStep: 10, feeFromLocation: false };
  if (!aircraftId || !ctx || !ctx.helipadId || !(Number(durationMin) > 0)) return EMPTY;
  let minPrice = null, minBroker = null, minOperator = null, minRate = null, minStep = 10;
  for (const op of operators || []) {
    const offers = (op.sightseeingHelipads || []).some(h => (h?.helipadRef?._ref || h?.helipadRef || h) === ctx.helipadId);
    if (!offers) continue;
    const acCfg = (op.acPricing || []).find(a => a && a.aircraft === aircraftId && a.sightseeingEnabled);
    if (!acCfg) continue;
    const rate = op.commissionRate ?? settingsRate ?? 10;
    const step = op.priceRoundingStep || 10;
    // Operator flat sightseeing price (sightseeingOverrides) for this origin pad +
    // duration (+ optional aircraft) — replaces the cost-model flight net (pad/pax
    // fees + special still on top). null → normal cost-model/rate computation.
    const ssOverride = matchSightseeingOverride(op.sightseeingOverrides, ctx.helipadId, durationMin, aircraftId);
    const overrideFlatNet = ssOverride ? sightseeingOverrideNet(ssOverride) : null;
    // sightseeing always prices through the cost-model chain (force the model) + the
    // SAME per-pad/per-pax fees the charter path charges (shared sightseeingBrokerNet).
    const res = sightseeingBrokerNet(acCfg, op.buffer, ctx, durationMin, op, feeClass, pax, overrideFlatNet);
    if (!res || !(res.netto > 0)) continue;
    const netto = res.netto;
    // Model B (owner decision 2026-06-16): the sightseeing operator payout rounds UP to
    // the next €100 (operatorFinalRounded) — same as the charter cost-model payout — and
    // the customer markup computes on that rounded payout via effectiveComputeNet (so no
    // double rounding, exactly like customerBaseTotal). brokerPrice stays the RAW netto.
    const cp = brokerToCustomer(effectiveComputeNet(netto), rate, VAT_RATE, step);
    if (cp != null && (minPrice == null || cp < minPrice)) {
      minPrice = cp; minBroker = netto; minRate = rate; minStep = step;
      minOperator = { _id: op.operatorId || null, commissionRate: (typeof op.commissionRate === 'number') ? op.commissionRate : null };
    }
  }
  if (minPrice == null) return EMPTY;
  return { price: minPrice, type: 'exact', brokerPrice: minBroker, operator: minOperator, rate: minRate, roundStep: minStep, feeFromLocation: false };
}

/**
 * Estimate the one-leg price for an aircraft on a from→to route, mirroring
 * the frontend's `estimatePrice()` fallback chain.
 *
 * Priority:
 *   1. exact direct route
 *   2. exact reverse route
 *   3. indicative direct route
 *   4. indicative reverse route
 *   5. interpolation across all routes with exact pricing for this aircraft
 *      (weighted by flight-time similarity)
 *   6. hourly-rate × flightMin / 60
 *
 * @param {Object} params
 * @param {string} params.aircraftId
 * @param {string} params.from
 * @param {string} params.to
 * @param {number} params.flightMin
 * @param {Object} params.directRoute — route doc for from→to, may be null
 * @param {Object} params.reverseRoute — route doc for to→from, may be null
 * @param {Array}  params.allRoutes — all routes with pricing (for interpolation)
 * @param {Object} params.aircraftDoc — aircraft doc with `hourlyRate`
 * @param {number} params.commPct — default commission percentage
 * @returns {{price: number|null, type: 'exact'|'indicative'|'estimated'|null}}
 */
export function estimateAircraftPrice({
  aircraftId, from, to, flightMin,
  directRoute, reverseRoute, allRoutes, aircraftDoc, commPct, locations,
  depCoords, arrCoords, operatorFilter, depId = null, arrId = null,
  flightDate = null,
}) {
  // op-pricing P6b — automatic-pricing context (base coords, speed, city
  // pickup/drop-off coords) for the engine. Uses the SAME cityCoords + home
  // base the client customer-path uses, so server validation == client price.
  //
  // Brief A — pad-exact override. When the caller supplies the chosen helipad
  // coordinates (depCoords/arrCoords), the engine prices base→PAD→PAD→base
  // instead of base→city→city→base, so the automatic route price reflects the
  // actual landing pads. cityCoords stays the fallback for legacy/no-pad
  // callers. The operatorFilter restricts the exact lookup to operators that
  // serve BOTH chosen pads, so client + server agree bit-for-bit.
  //
  // Seasonal base (Feature B1): the positioning leg starts at the base in effect
  // on the REQUESTED FLIGHT DATE — a basePeriods[] window covering flightDate
  // overrides homeBase; no date / no covering window → homeBase (unchanged).
  // resolveBaseForDate is the SHARED resolver the client funnel uses too, so the
  // base-determining date drives an identical price on both sides.
  const hb = resolveBaseForDate(aircraftDoc, flightDate);
  const autoBase = (hb && hb.lat != null && hb.lng != null)
    ? { _id: hb.id || aircraftId, name: hb.name, type: hb.type, lat: hb.lat, lng: hb.lng } : null;
  const autoSpeed = Number(aircraftDoc?.speedKmh) || 0;
  const locs = Array.isArray(locations) ? locations : [];
  const depPt = (depCoords && depCoords.lat != null && depCoords.lng != null) ? depCoords : cityCoords(locs, from);
  const arrPt = (arrCoords && arrCoords.lat != null && arrCoords.lng != null) ? arrCoords : cityCoords(locs, to);
  const ctxDirect = { base: autoBase, speedKmh: autoSpeed, pickup: depPt, dropoff: arrPt };
  const ctxReverse = { base: autoBase, speedKmh: autoSpeed, pickup: arrPt, dropoff: depPt };
  // 1. Exact direct
  if (directRoute) {
    const p = getRoutePriceFromDoc(aircraftId, directRoute, commPct, ctxDirect, operatorFilter, depId, arrId);
    if (p.price && p.type === 'exact') return p;
  }
  // 2. Exact reverse
  if (reverseRoute) {
    const p = getRoutePriceFromDoc(aircraftId, reverseRoute, commPct, ctxReverse, operatorFilter, arrId, depId);
    if (p.price && p.type === 'exact') return p;
  }
  // 3. Indicative direct
  if (directRoute) {
    const ind = (directRoute.indicativePricing || []).find(x => x.aircraft === aircraftId);
    if (ind && ind.price) return { price: ind.price, type: 'indicative' };
  }
  // 4. Indicative reverse
  if (reverseRoute) {
    const ind = (reverseRoute.indicativePricing || []).find(x => x.aircraft === aircraftId);
    if (ind && ind.price) return { price: ind.price, type: 'indicative' };
  }
  // 5. Interpolation across all routes priced for this aircraft
  if (flightMin > 0 && Array.isArray(allRoutes)) {
    const routesWithPrice = [];
    for (const r of allRoutes) {
      const rp = getRoutePriceFromDoc(aircraftId, r, commPct);
      if (rp.price && rp.type === 'exact' && r.flightMin > 0) {
        routesWithPrice.push({ price: rp.price, flightMin: r.flightMin });
      }
    }
    if (routesWithPrice.length >= 2) {
      let totalWeight = 0, weightedSum = 0;
      for (const r of routesWithPrice) {
        const ratePerMin = r.price / r.flightMin;
        const diff = Math.abs(r.flightMin - flightMin);
        const w = 1 / (1 + diff / 10);
        weightedSum += ratePerMin * w;
        totalWeight += w;
      }
      const estimatedRate = weightedSum / totalWeight;
      const estimated = Math.ceil(estimatedRate * flightMin / 10) * 10;
      return { price: estimated, type: 'estimated' };
    }
  }
  // 6. Hourly-rate fallback — rate × time, so floor the block minutes to whole
  // minutes first (cost-model / Automatic parity, 2026-06-15; consistency only,
  // this is the rough estimator for routes with no exact compute).
  if (aircraftDoc && aircraftDoc.hourlyRate && flightMin > 0) {
    const estimated = Math.ceil((aircraftDoc.hourlyRate * Math.floor(flightMin) / 60) / 10) * 10;
    return { price: estimated, type: 'estimated' };
  }
  return { price: null, type: null };
}

/**
 * Sum the price of selected extras, mirroring the frontend's
 * `calcExtrasTotal()`.
 *
 * Two paths:
 *  - Operator extras (P2 #9 phase 3) — items carry `operatorId`. Validated
 *    against that operator's LIVE `extras[]` catalog: a missing id, an
 *    inactive entry, or a price differing from the catalog is a HARD
 *    rejection (`rejection` field set — the caller returns 422). The leg
 *    multiplier mirrors the client exactly: `leg === 'both'` → ×2, else ×1.
 *  - Legacy global extras (no `operatorId`) — looked up in the global
 *    `*[_type=="extra"]` catalog (empty in production; kept for back-compat).
 *
 * The client-supplied `price` is never trusted for the total — the
 * authoritative catalog price is always used.
 *
 * @param {Array} extrasArray — `booking.extras` from the client
 * @param {Array} extrasCatalog — global extras `{id,name,price,perLeg}`
 * @param {Array} operatorExtras — `[{ _id, commissionRate, extras:[{id,name,price,active,perLeg,kind}] }]`
 * @param {Object} settings — siteSettings (for the commission-rate fallback)
 * @param {boolean} isRoundTrip
 * @returns {{total:number, brokerTotal:number, mismatches:Array, reconciled:Array, rejection:Object|null}}
 */
export function computeExtrasTotal(extrasArray, extrasCatalog, operatorExtras, settings, isRoundTrip) {
  let total = 0;        // customer-facing total
  let brokerTotal = 0;  // operator-earn total (Σ per-extra GROSS broker = round(net×1.13))
  const mismatches = [];
  const reconciled = [];
  let rejection = null; // first hard failure (forgery / tampering / stale)

  for (const item of extrasArray || []) {
    if (!item || typeof item !== 'object' || !item.id) continue;

    // ── Operator extras — validate against the operator's live catalog ──
    if (item.operatorId) {
      const opDoc = (operatorExtras || []).find(o => o && o._id === item.operatorId);
      const catalogExtra = opDoc && Array.isArray(opDoc.extras)
        ? opDoc.extras.find(e => e && e.id === item.id)
        : null;
      if (!catalogExtra) {
        rejection = rejection || { reason: 'extra_not_in_catalog', id: item.id, operatorId: item.operatorId };
        continue;
      }
      if (catalogExtra.active === false) {
        rejection = rejection || { reason: 'extra_inactive', id: item.id, operatorId: item.operatorId };
        continue;
      }
      // P2 #9 phase 4 — the operator extra `price` is the BROKER price.
      // Phase 1b — operator extras are stored NET (excl. VAT), like the rest of
      // the charter chain, so the CUSTOMER price has a single consistent VAT step
      // via the default brokerToCustomer (net → markup → VAT 13% → round). The
      // OPERATOR side mirrors the route exactly (MODEL 1): the broker payout is the
      // NET extra reconstructed to GROSS = round(net × (1+VAT)) PER extra, so both
      // brokerTotal and the stored per-extra brokerPrice are gross — consistent with
      // landingFeesBroker (already gross) and with operatorEarn/ssOperatorEarn, which
      // add this gross total to the grossed route. Restores byte-identical pre-1b
      // operator economics (SMOKE 2nd-pilot net 265.49 → gross 300; customer 330).
      const rate = getEffectiveCommission(opDoc, settings);
      const brokerNet = Number(catalogExtra.price) || 0;
      const customerPrice = brokerToCustomer(brokerNet, rate);
      const brokerGross = Math.round(brokerNet * (1 + VAT_RATE));
      const clientPrice = Number(item.price) || 0;
      if (customerPrice !== clientPrice) {
        rejection = rejection || { reason: 'extra_price_mismatch', id: item.id, operatorId: item.operatorId, clientPrice, serverPrice: customerPrice };
        continue;
      }
      // leg='both' → ×2; mirrors the client calcExtrasTotal exactly.
      const legMult = item.leg === 'both' ? 2 : 1;
      total += customerPrice * legMult;
      brokerTotal += brokerGross * legMult;
      reconciled.push({
        _key: newKey(),
        id: item.id,
        name: catalogExtra.name || item.name || item.id,
        price: customerPrice,
        brokerPrice: brokerGross,
        kind: catalogExtra.kind || item.kind || 'service',
        perLeg: !!catalogExtra.perLeg,
        leg: item.leg || 'single',
        operatorId: item.operatorId,
      });
      continue;
    }

    // Hardening (extras VAT audit #2): only OPERATOR-scoped extras are billable.
    // Every extra must carry an operatorId so it runs through the single
    // NET → markup → VAT chain above (customer brokerToCustomer + operator
    // round(net × (1+VAT))). A global / no-operatorId extra has no NET source and
    // no operator to pay, so it is intentionally NOT priced — it can never be
    // billed VAT-free or commission-free. (The old global-catalog branch charged
    // catalog.price raw, with no VAT and no commission; there are 0 global `extra`
    // docs, so removing it changes no real behaviour and closes that latent path.)
    // The item simply falls through here — no contribution, not added to reconciled.
  }
  return { total, brokerTotal, mismatches, reconciled, rejection };
}

/**
 * Customer-facing BASE price for ONE direction = route flight price + per-pad
 * NET fees, run through the SAME single markup + VAT chain — WITHOUT extras /
 * second pilot. This is the exact piece computeExpectedTotal folds into the
 * customer expectedTotal (basePrice + helipadFees), extracted so the read-only
 * /api/aircraft/quote endpoint can return customerTotal / customerRtTotal from
 * the IDENTICAL computation. Single source of truth ⇒ a quoted candidate
 * validates at booking (the price-gate can never disagree with the quote).
 *
 * Pure: derives only from a precomputed `base` (estimateAircraftPrice result),
 * the chosen pad locations, and the operator home base. No I/O.
 *
 * @param {Object}  params
 * @param {Object}  params.base — estimateAircraftPrice() result (price/brokerPrice/operator/rate/...)
 * @param {Object}  params.depLocation — departure pad location doc (needs padFees, _id)
 * @param {Object}  params.arrLocation — arrival pad location doc (needs padFees, _id)
 * @param {Object}  [params.baseLocation] — operator home base (for the far-base positioning touch)
 * @param {boolean} [params.isRoundTrip] — round-trip touch geometry when true
 * @param {number}  [params.settingsRate] — platform commission %, fallback when the row has none
 * @returns {{
 *   customerTotal: number|null, basePrice: number|null, helipadFees: number,
 *   rtBrokerNet: number|null, padFeesNet: number, netBaseForPad: number|null,
 *   routeRate: number, routeRoundStep: number
 * }}
 */
export function customerBaseTotal({ base, depLocation, arrLocation, baseLocation = null, isRoundTrip = false, settingsRate = 10, feeClass = null, pax = 1 }) {
  // Rate / rounding-step = winning row's operator values, platform default else.
  const routeRate = (base && base.rate != null) ? base.rate : settingsRate;
  const routeRoundStep = (base && base.roundStep) || 10; // Phase 1b — winning op's rounding step
  // Customer base flight price. Round trip honours an explicit returnBrokerPrice
  // (carried by getRoutePriceFromDoc) via getReturnPrice; falls back to one-way×2.
  const basePrice = (base && base.price != null)
    ? (isRoundTrip ? getReturnPrice(base, base.price, routeRate, routeRoundStep, base.roundTripPrice) : base.price)
    : null;
  // True per-leg round-trip broker netto behind the customer RT base — precedence
  // mirrors getReturnPrice: override > compute (A→B→A, no phantom ferry) > ×2.
  const rtBrokerNet = (isRoundTrip && base && base.brokerPrice != null)
    ? ((Number.isFinite(Number(base.returnBrokerPrice)) && Number(base.returnBrokerPrice) > 0)
        ? Number(base.returnBrokerPrice)
        : (base.roundTripBrokerNet != null && base.roundTripBrokerNet > 0)
          ? base.roundTripBrokerNet
          : base.brokerPrice * 2)
    : null;
  // Per-pad NET fees along the real legs (unified model). One source for ALL
  // modes; operator override (brokerFee-only here) → falls back to location.padFees.
  const opForHelipad = (base && base.operator) || null;
  const helipadRate = (opForHelipad && typeof opForHelipad.commissionRate === 'number')
    ? opForHelipad.commissionRate
    : settingsRate;
  const round100 = !!(base && base.roundFinal100); // D) compute modes round the combined Final to €100
  const padTouches = buildPadTouches({ baseLocation, depLocation, arrLocation, operator: opForHelipad, isRoundTrip });
  const padFeesNet = routePadFeesNet(padTouches, feeClass);
  // B) HELISTAR per-pax airport fees — compute modes only (cost-model + automatic);
  // manual / route-override are untouched. × pax, pax-carrying legs only.
  const paxFeesNet = round100 ? routePaxFeesNet(padTouches, feeClass, pax) : 0;
  // NET route base behind the customer basePrice (one-way: brokerPrice; RT: the
  // per-leg rtBrokerNet — same value getReturnPrice marked up into basePrice).
  const netBaseForPad = isRoundTrip
    ? (rtBrokerNet != null ? rtBrokerNet : (base && base.brokerPrice != null ? base.brokerPrice * 2 : null))
    : (base ? base.brokerPrice : null);
  // D) Model B (compute modes): round the operator Final incl VAT (over the COMBINED
  // net = flight + pad + pax) UP to €100 at the END, then the markup computes on it —
  // so the Final is a clean €100 (fixes the fees-escape-rounding bug 5418.68→5400).
  // effectiveComputeNet(combined) feeds the EXISTING brokerToCustomer → no double-VAT.
  // basePrice stays the flight-only (already-rounded) customer figure for display;
  // helipadFees is the remainder (pad + pax + the re-rounding delta). Manual mode is
  // byte-identical to before (per-pad markup delta, no pax fees, no €100 rounding).
  // HELISTAR "Special discount" (operator broker special) — the LAST NET-stage op,
  // applied to the COMBINED net (flight + pad + per-pax) ONCE, before the markup/VAT.
  // Only a pure cost-model compute carries it (base.brokerDiscount; null otherwise →
  // no-op). costBrokerBreakdown's flight net excludes it, so applying it here on the
  // combined net is the single application (no double-count). combinedNet is returned
  // so computeExpectedTotal reuses the IDENTICAL after-special net for the operator
  // payout → customer price == payout by construction.
  const brokerSpecial = (base && base.brokerDiscount) || null;
  let helipadFees, customerTotal, combinedNet = null;
  if (round100 && netBaseForPad != null) {
    combinedNet = applyBrokerSpecial(netBaseForPad + padFeesNet + paxFeesNet, brokerSpecial);
    customerTotal = brokerToCustomer(effectiveComputeNet(combinedNet), helipadRate, VAT_RATE, routeRoundStep);
    helipadFees = basePrice != null ? customerTotal - basePrice : 0;
  } else {
    helipadFees = (netBaseForPad != null && padFeesNet > 0)
      ? brokerToCustomer(netBaseForPad + padFeesNet, helipadRate, VAT_RATE, routeRoundStep) - brokerToCustomer(netBaseForPad, helipadRate, VAT_RATE, routeRoundStep)
      : 0;
    customerTotal = basePrice != null ? basePrice + helipadFees : null;
  }
  return { customerTotal, basePrice, helipadFees, rtBrokerNet, padFeesNet, paxFeesNet, netBaseForPad, combinedNet, routeRate, routeRoundStep, round100 };
}

/**
 * Orchestrator: compute expected total price for a booking, to be compared
 * against the client-submitted `booking.totalPrice`.
 *
 * Assumes the caller has already determined that validation is applicable
 * (i.e. not an isQuoteRequest, not a customroute, totalPrice > 0).
 *
 * @param {Object} params
 * @param {Object} params.booking — the client-submitted booking body
 * @param {Object} params.directRoute — Sanity doc for booking.from → booking.to
 * @param {Object} params.reverseRoute — Sanity doc for booking.to → booking.from
 * @param {Array}  params.allRoutes — all routes with pricing (for interpolation)
 * @param {Object} params.aircraftDoc — aircraft doc (for hourlyRate fallback)
 * @param {Array}  params.extrasCatalog — all extras from Sanity
 * @param {Object} params.settings — siteSettings (defaultCommissionPct)
 * @returns {{
 *   expectedTotal: number|null,
 *   basePrice: number|null,
 *   basePriceType: 'exact'|'indicative'|'estimated'|null,
 *   extrasTotal: number,
 *   extrasMismatches: Array,
 *   reconciledExtras: Array,
 * }}
 */
export function computeExpectedTotal({
  booking, directRoute, reverseRoute, allRoutes, aircraftDoc, extrasCatalog, operatorExtras, settings,
  depLocation, arrLocation, baseLocation = null, locations, operatorFilter, sightseeingOperators = [],
}) {
  // P2 #9 phase 4 — platform commission rate (markup model), default 10.
  const settingsRate = settings?.commissionRate ?? 10;

  // ── Sightseeing (Phase 2) — ALWAYS compute via the cost-model chain (no
  // route/manual/override). Loop at the chosen helipad (booking.depPad) with
  // flightHours = positioning (base→pad→base) + chosen duration. No separate
  // helipad fees (the cost-model airport chain carries them — feeFromLocation
  // is false, so the Brief-2 location.fee side never applies → no double-count).
  // one-way/round-trip paths below are untouched. ──
  if (booking.tripType === 'sightseeing') {
    const durationMin = Number(booking.tourDuration) || 0;
    // Seasonal base (Feature B1): positioning ferries from the base in effect on
    // the flight date (basePeriods[] over homeBase), via the shared resolver.
    const hb = resolveBaseForDate(aircraftDoc, booking.date);
    const ssCtx = {
      // base carries the FULL resolved base doc (_id, padFees) so the homeBase touch
      // fees (positioning departure + ferry-home landing) resolve — the heli ferries
      // base→pad→loop→pad→base, touching the base too.
      base: (hb && hb.lat != null && hb.lng != null) ? hb : null,
      speedKmh: Number(aircraftDoc?.speedKmh) || 0,
      // pickup carries the FULL pad doc (_id, name, lat/lng, padFees) so the per-pad
      // sightseeing fees resolve (departure/landing/handling/special + per-pax), like
      // the charter path — not just coords. helipadId still drives the offers gate.
      pickup: (depLocation && depLocation.lat != null && depLocation.lng != null) ? depLocation : null,
      helipadId: depLocation?._id || null,
    };
    const ssPax = Number(booking.pax) > 0 ? Number(booking.pax) : 1;
    const ss = getSightseeingPrice(booking.aircraft, sightseeingOperators, ssCtx, durationMin, settingsRate, feeClassOf(aircraftDoc), ssPax);
    const { total: ssExtrasTotal, brokerTotal: ssExtrasBroker, mismatches: ssMis, reconciled: ssRec, rejection: ssRej } =
      computeExtrasTotal(booking.extras, extrasCatalog, operatorExtras, settings, false);
    // Legacy flat second-pilot surcharge removed (2026-06-11) — second pilot is an operator extra (in ssExtrasTotal).
    const ssTotal = ss.price != null ? ss.price + ssExtrasTotal : null;
    // ssExtrasBroker is already GROSS (computeExtrasTotal grosses each extra net×1.13);
    // add it to the grossed sightseeing route broker → operator payout includes extras VAT.
    // Model B (2026-06-16): sightseeing operator payout = the operator Final incl VAT
    // rounded UP to the next €100 — operatorFinalRounded, the SAME helper the charter
    // cost-model payout uses (operatorEarn below), no divergent rounding. brokerPrice is
    // the raw netto; the customer markup already computed on the rounded payout (above).
    const ssOperatorEarn = (ss.price != null && ss.brokerPrice != null)
      ? operatorFinalRounded(ss.brokerPrice) + ssExtrasBroker : null;
    return {
      expectedTotal: ssTotal, basePrice: ss.price, basePriceType: ss.price != null ? 'exact' : null,
      extrasTotal: ssExtrasTotal, helipadFees: 0, helipadBroker: 0,
      operatorEarn: ssOperatorEarn,
      brokerCommission: (ssTotal != null && ssOperatorEarn != null) ? ssTotal - ssOperatorEarn : null,
      extrasMismatches: ssMis, extrasRejection: ssRej, reconciledExtras: ssRec,
      depHelipadFee: 0, arrHelipadFee: 0, revenueLegCount: 1, isRoundTrip: false,
    };
  }
  // Brief A — pad-exact pricing. Use the chosen helipads' coordinates (resolved
  // from the dep/arr location docs) so the server recomputes the SAME automatic
  // route price the client showed for the selected pads. operatorFilter (built
  // by the caller from served-pad sets) restricts the exact lookup to operators
  // serving both pads — client == server by construction.
  const depCoords = (depLocation && depLocation.lat != null && depLocation.lng != null)
    ? { lat: depLocation.lat, lng: depLocation.lng } : null;
  const arrCoords = (arrLocation && arrLocation.lat != null && arrLocation.lng != null)
    ? { lat: arrLocation.lat, lng: arrLocation.lng } : null;
  const base = estimateAircraftPrice({
    aircraftId: booking.aircraft,
    from: booking.from,
    to: booking.to,
    flightMin: booking.flightMin || directRoute?.flightMin || reverseRoute?.flightMin || 0,
    directRoute, reverseRoute, allRoutes, aircraftDoc, commPct: settingsRate, locations,
    depCoords, arrCoords, operatorFilter,
    depId: depLocation?._id || null, arrId: arrLocation?._id || null, // route-override pad match
    flightDate: booking.date || null, // seasonal base resolved for the flight date
  });

  const isRoundTrip = booking.tripType === 'roundtrip';
  // Customer-facing base (route flight + per-pad NET fees, ONE markup+VAT) for
  // this direction — derived from the SHARED customerBaseTotal helper so the
  // read-only /api/aircraft/quote endpoint returns the IDENTICAL number (no
  // drift; a quoted candidate validates here). basePrice / rtBrokerNet /
  // padFeesNet / helipadFees below are the pieces the expectedTotal + operator
  // payout math read — byte-identical to the prior inline computation.
  const bookingPax = Number(booking.pax) > 0 ? Number(booking.pax) : 1; // B) per-pax airport fees use the real pax count
  const cb = customerBaseTotal({ base, depLocation, arrLocation, baseLocation, isRoundTrip, settingsRate, feeClass: feeClassOf(aircraftDoc), pax: bookingPax });
  const basePrice = cb.basePrice;
  const rtBrokerNet = cb.rtBrokerNet;

  const { total: extrasTotal, brokerTotal: extrasBrokerTotal, mismatches: extrasMismatches,
          reconciled: reconciledExtras, rejection: extrasRejection } =
    computeExtrasTotal(booking.extras, extrasCatalog, operatorExtras, settings, isRoundTrip);

  // Legacy flat second-pilot surcharge REMOVED (2026-06-11). The real Second
  // Pilot runs as an operator extra (operator.extras[] kind='pilot', NET) through
  // computeExtrasTotal above — markup + VAT exactly once. The old
  // booking.secondPilot flag + settings.secondPilotPrice added a FLAT customer
  // amount with NO markup/VAT chain; the client stopped sending the flag at the
  // second-pilot→extras migration, so the branch was dead — but a direct API call
  // could still have tipped an un-VAT'd flat 300 into the total. Killed pre-Mollie-live.

  // ── Helipad / per-pad fees — sourced from customerBaseTotal (above), the
  // SINGLE chain shared with /api/aircraft/quote. Pad fees are LIVE in prod
  // (per-pad NET defaults on location.padFees, optional operator overrides):
  // a route touching a fee-pad adds padFeesNet > 0; a no-fee route gives
  // padFeesNet 0 → helipadFees 0 → base price unchanged. revenueLegCount (2 for
  // the symmetric round trip, 1 one-way) + helipadBroker are derived from the
  // SAME padFeesNet so booking.legs[] + the operator payout stay byte-identical.
  const revenueLegCount = isRoundTrip ? 2 : 1;
  const padFeesNet = cb.padFeesNet;        // NET Σ of the direction's pad-fee touches
  const helipadFees = cb.helipadFees;      // customer-facing pad delta (single markup+VAT)
  // Operator pad earn: NET pad fees → GROSS (× 1.13) for the payout/invoice.
  const helipadBroker = Math.round(padFeesNet * (1 + VAT_RATE));
  // Per-pad customer fees for booking.legs[] are wired in a later sub-chunk
  // (bookings-create/money-flow); still 0 placeholders here — the live pad total
  // is carried by helipadFees/helipadBroker above; only the per-leg split is not
  // yet decomposed at this point.
  const depFee = 0;
  const arrFee = 0;

  const expectedTotal = basePrice != null
    ? basePrice + extrasTotal + helipadFees
    : null;

  // ── Operator earn — sum of broker prices, no further deduction.
  // Computable only when the route price is exact (broker price known).
  // For indicative/estimated pricing it stays null → the booking falls
  // back to the legacy operatorPayout() computation.
  let operatorEarn = null;
  if (base.type === 'exact' && base.brokerPrice != null) {
    // Per-leg migration: round-trip operator netto = the SAME rtBrokerNet that
    // drives the customer basePrice (true A→B→A, no phantom-ferry double-count).
    // Was base.brokerPrice + (returnBrokerPrice ?? base.brokerPrice) — that
    // doubled the ferry and disagreed with getReturnPrice's override semantics.
    const routeBrokerNet = isRoundTrip
      ? (rtBrokerNet != null ? rtBrokerNet : base.brokerPrice * 2)
      : base.brokerPrice;
    // Phase 1a (MODEL 1, owner-confirmed) — operator economics are UNCHANGED by
    // the net-migration. Route broker config is now stored NET, so the operator's
    // actual payout (what we transfer + they invoice incl. VAT) is reconstructed
    // to GROSS = net × (1 + VAT), rounded to whole EUR to restore the original
    // (whole-EUR) config values exactly. helipadBroker is already gross (above);
    // extrasBrokerTotal is likewise already gross — computeExtrasTotal reconstructs
    // each NET extra to gross (round(net×1.13)) the same way (Phase 1b net-migrated
    // extras too; the operator side mirrors the route — fixed 2026-06-06; previously
    // the net extra was added to the gross route, underpaying the extras VAT). This
    // keeps booking.operatorPayout byte-identical to pre-migration (Athens→Antiparos
    // operator 3200; an added extra restores its pre-1b whole-EUR gross).
    // Pad fees fold into the net BEFORE the single VAT rounding (one rounding at
    // the end) → operator payout = (routeNet + padNet + paxNet) × 1.13. helipadBroker
    // is already part of this; NOT added again (no double-count). 0 pad → unchanged.
    // D) Model B (compute modes, cb.round100): the operator Final incl VAT (over the
    // COMBINED net incl pad + per-pax airport fees AND the broker special discount) is
    // rounded UP to €100 — the operator-payout record == the simulator "Final / Payout"
    // == the rounded value == the customer-price basis. cb.combinedNet is the SAME
    // after-special net customerBaseTotal marked up, so price == payout by construction.
    // Manual / route-override: byte-identical round((routeNet+padNet)×1.13), no pax/special.
    const routeBrokerGross = cb.round100
      ? operatorFinalRounded(cb.combinedNet)
      : Math.round((routeBrokerNet + padFeesNet) * (1 + VAT_RATE));
    operatorEarn = routeBrokerGross + extrasBrokerTotal;
  }
  const brokerCommission = (expectedTotal != null && operatorEarn != null)
    ? expectedTotal - operatorEarn
    : null;

  return {
    expectedTotal,
    basePrice,
    basePriceType: base.type,
    extrasTotal,
    helipadFees,
    helipadBroker,
    operatorEarn,
    brokerCommission,
    extrasMismatches,
    extrasRejection,
    reconciledExtras,
    // Per-leg authority — the per-pad customer fee for booking.legs[] (still 0
    // placeholders here; the per-leg split is not yet decomposed — the live pad
    // total is helipadFees/helipadBroker above) + revenue leg count, so
    // bookings-create can write booking.legs[].airport*Fee = the charged value
    // (NOT location.fee) and money-flow.js decomposes from the SAME source.
    depHelipadFee: depFee,
    arrHelipadFee: arrFee,
    revenueLegCount,
    isRoundTrip,
  };
}

/**
 * Tolerance for price validation based on the price-derivation type.
 *   exact: 2%
 *   estimated / indicative / other: 5%
 */
export function toleranceForPriceType(type) {
  return type === 'exact' ? 0.02 : 0.05;
}

// ═══════════════════════════════════════════════════════════════════════
// P2 #9 PHASE 4 — broker-price commission helpers.
//
// NOT yet wired into bookings-create.js or App.jsx — that is phase 4
// commit 2. Added here in commit 1 so the diagnosis + the helpers ship
// together for review.
//
// SEMANTIC NOTE — these helpers implement the NEW model: the operator's
// input is the broker price (what they receive in full), and the customer
// price is that broker price plus a commission MARKUP:
//     customer = ceil(broker × (1 + rate/100) / 10) × 10
// This differs from the legacy logic elsewhere in this file
// (getRoutePriceFromDoc / getReturnPrice / helipad-fee.js), which derives
// customer = ceil(broker / (1 − rate/100) / 10) × 10 — i.e. commission as
// a % of the customer price. Commit 2 migrates the live paths onto the
// markup model.
// ═══════════════════════════════════════════════════════════════════════

/**
 * Resolve the effective commission rate (%) for an operator.
 * Resolution: operator override → platform default → 10.
 *
 * @param {Object|null} operator — operator doc/projection with `commissionRate`
 * @param {Object|null} settings — siteSettings with `commissionRate`
 * @returns {number} commission rate as a percentage (e.g. 10)
 *
 * @example getEffectiveCommission({ commissionRate: 12 }, { commissionRate: 10 }) // → 12
 * @example getEffectiveCommission({}, { commissionRate: 8 })                       // → 8
 * @example getEffectiveCommission(null, null)                                     // → 10
 */
export function getEffectiveCommission(operator, settings) {
  return operator?.commissionRate ?? settings?.commissionRate ?? 10;
}

// ═══════════════════════════════════════════════════════════════════════
// VAT — central constant (Phase 1a net-migration, 2026-06-03).
//
// Stored operator pricing config is NET (excl. VAT). VAT is applied EXACTLY
// ONCE, at the end of the customer-price chain, inside brokerToCustomer. This
// is the single source of truth for the rate — never hardcode 0.13 / 1.13 in
// the pricing path; import VAT_RATE. Greek domestic helicopter passenger
// transport = 13%. (siteSettings.vatRate mirrors this for customer-facing
// display copy; the calc uses this constant so client + server never drift.)
// ═══════════════════════════════════════════════════════════════════════
export const VAT_RATE = 0.13;

/**
 * Convert an operator NET broker price into the customer-facing GROSS price:
 * apply the commission markup, then VAT, then round UP to the nearest €10.
 *
 *     customerNet   = brokerNet × (1 + rate/100)
 *     customerGross = customerNet × (1 + vatRate)
 *     customer      = ceil(customerGross / 10) × 10
 *
 * VAT is applied EXACTLY ONCE here, at the end of the chain (Phase 1a). The
 * gross is rounded to integer cents BEFORE the ceil-to-€10 to kill binary
 * float noise (e.g. 3519.9999998 must not ceil to 3530) — the same defensive
 * intent as the pre-net integer-math form, generalised for the ×(1+vat) step.
 *
 * Equivalence (the migration safety-net): for a value V that was stored
 * incl-VAT pre-migration and is migrated to net = V/1.13,
 *   ceil10(net × markup × 1.13) == ceil10(V × markup)
 * so every customer price is preserved (within the ceil-€10 step).
 *
 * `vatRate` defaults to VAT_RATE: a broker-NET input yields a gross customer
 * price. Callers whose broker input is NOT net-of-VAT (operator extras, kept
 * gross in Phase 1a) pass `vatRate: 0` to opt out — that path reproduces the
 * exact pre-migration integer-math result.
 *
 * The operator always earns their broker amount — the round-up margin is
 * absorbed into the platform commission, never deducted from the operator.
 *
 * @param {number} brokerPrice — operator NET broker price (EUR, excl. VAT)
 * @param {number} rate — commission rate as a percentage (e.g. 10)
 * @param {number} [vatRate=VAT_RATE] — VAT fraction (0.13); pass 0 to skip VAT
 * @returns {number} customer-facing gross price, multiple of 10; 0 when broker ≤ 0
 *
 * @example brokerToCustomer(2831.86, 10)    // net → 2831.86×1.10×1.13 → ceil10 = 3520
 * @example brokerToCustomer(398.23, 10)     // net → 398.23×1.10×1.13  → ceil10 = 500
 * @example brokerToCustomer(300, 10, 0)     // vatRate 0 (custom-quote entry, already gross) → 330
 * @example brokerToCustomer(0, 10)          // → 0
 */
export function brokerToCustomer(brokerPrice, rate, vatRate = VAT_RATE, roundStep = 10) {
  if (!brokerPrice || brokerPrice <= 0) return 0;
  // Phase 1b — configurable ceil step (operator setting, €10 default / 50 / 100),
  // applied at the customer-gross (chain step 9). Unset/other → 10 (unchanged).
  const step = (roundStep === 50 || roundStep === 100) ? roundStep : 10;
  // brokerPrice × (100+rate) × (1+vat) = customerGross × 100 (integer cents).
  // Math.round() collapses sub-cent float noise; ceil(cents / (step×100)) × step.
  const grossCents = Math.round(brokerPrice * (100 + rate) * (1 + vatRate));
  return Math.ceil(grossCents / (step * 100)) * step;
}

/**
 * Server-authoritative CUSTOMER price for ONE operator extra: the operator's NET
 * (broker) extra price marked up at the operator's EFFECTIVE commission + VAT.
 * Mirrors computeExtrasTotal's per-extra customerPrice EXACTLY (same
 * brokerToCustomer, default €10 step) so the value the public-DTO exposes to the
 * funnel is byte-identical to what bookings-create validates. Exposing this
 * finished price lets the client stop recomputing with a fallback commission —
 * commissionRate itself stays stripped from the public payload (operator
 * boundary intact); only the finished customer price is shared.
 */
export function extraCustomerPrice(brokerNet, operator, settings) {
  return brokerToCustomer(Number(brokerNet) || 0, getEffectiveCommission(operator, settings));
}

/**
 * Build an itemised customer breakdown from a list of broker-priced items.
 *
 * @param {Array<{label:string, brokerPrice:number}>} items
 * @param {number} rate — commission rate as a percentage
 * @returns {{
 *   lines: Array<{label:string, broker:number, customer:number}>,
 *   totalBroker:number, totalCustomer:number, commission:number
 * }}
 *
 * @example
 *   customerBreakdown([
 *     { label: 'Charter Athens → Antiparos', brokerPrice: 1600 },
 *     { label: 'Second Pilot',               brokerPrice: 200 },
 *   ], 10)
 *   // → lines: [{...,customer:1760}, {...,customer:220}],
 *   //   totalBroker: 1800, totalCustomer: 1980, commission: 180
 */
export function customerBreakdown(items, rate) {
  const lines = (items || []).map(i => ({
    label: i.label,
    broker: i.brokerPrice,
    customer: brokerToCustomer(i.brokerPrice, rate),
  }));
  const totalBroker = lines.reduce((s, l) => s + l.broker, 0);
  const totalCustomer = lines.reduce((s, l) => s + l.customer, 0);
  return { lines, totalBroker, totalCustomer, commission: totalCustomer - totalBroker };
}
